// hooks/useCreateBudgetFlow.js
import { useCallback, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import api from '../../config/api';
import apiv2 from '../../config/apiv2';
import { DASHBOARD_PATH } from '../../contants';
import {
  changeIsConfirm,
  changeSetCampaignTab,
  changeStep,
  resetState,
  setCurrentStep,
} from '../../features/createBudget/createBudgetSlice';
import { setViewOptions } from '../../features/dashboard/dashboardSlice';
import { setIsNewDataSource } from '../../features/dataSource/dataSourceSlice';
import { setMe } from '../../features/me/meSlice';
import { getMinuteDiffWithNow, getViewOptions } from '../../helpers';
import {
  failCreateBudget,
  failUpdateBudget,
  successCreateBudget,
  successUpdateBudget,
} from '../../helpers/notyf';

export default function useCreateBudgetFlow() {
  const dispatch = useDispatch();
  const params = useParams();
  const navigate = useNavigate();
  const location = useLocation();

  const { formData, tab, currentStep, choosenCampaigns } = useSelector(
    s => s.createBudget,
  );
  const { viewOptions } = useSelector(s => s.dashboard);

  useEffect(
    () => () => {
      dispatch(resetState());
    },
    [],
  );

  useEffect(() => {
    if (params.id && !isNaN(params.id)) {
      if (params.step === 'campaigns') {
        dispatch(changeStep('campaigns'));
        dispatch(setCurrentStep(3));
        dispatch(changeSetCampaignTab('campaigns'));
      } else {
        dispatch(setCurrentStep(2));
      }
    }
    dispatch(changeStep(location.pathname.split('/')[2]));
  }, [location, params.id]);

  useEffect(() => {
    if (currentStep !== 3) return;
    api.GET_DATA_SOURCES().then(res => {
      if (!res?.length) return;
      res.sort((a, b) => new Date(b.created_at) - new Date(a.created_at));
      const ts = new Date(res[0].created_at).getTime();
      const minuteDiff = getMinuteDiffWithNow(ts);
      dispatch(setIsNewDataSource(minuteDiff < 20));
    });
  }, [currentStep]);

  const handleClosePopup = useCallback(() => {
    dispatch(changeIsConfirm(false));
  }, []);

  const handleConfirm = useCallback(async () => {
    const isEdit = params.id && !isNaN(params.id);
    try {
      const base = {
        client_id: formData.client.value,
        amount: +formData.amount,
        name: formData.name,
        type: tab.charAt(0).toUpperCase() + tab.slice(1),
        campaigns_id: choosenCampaigns.map(c => c.id),
        renew_type: formData.renew_type,
      };

      if (tab === 'monthly')
        base.month_start_day = formData.month_start_day.value;
      if (tab === 'weekly') base.week_start_day = formData.week_start_day.value;
      if (tab === 'custom') {
        const toISO = d =>
          typeof d === 'string'
            ? d
            : `${d.getFullYear()}-${d.getMonth() + 1}-${d.getDate()}`;
        base.start_date = toISO(formData.start_date);
        base.end_date = toISO(formData.end_date);
        base.renew_type = formData.type.label;
      }

      if (isEdit) {
        await api.UPDATE_BUDGET(base, params.id);
        successUpdateBudget();
      } else {
        const res = await api.CREATE_BUDGET({
          ...base,
          renew_type: base.renew_type || 'Recurring',
        });
        if (res?.data) {
          localStorage.setItem(
            'newBudget',
            JSON.stringify({ id: res.data.id, syncStatus: false }),
          );
        }
        successCreateBudget();
        apiv2.GET_ME().then(r => dispatch(setMe(r.data)));
      }

      const newView = await getViewOptions(viewOptions);
      dispatch(setViewOptions(newView));
      dispatch(changeIsConfirm(false));
      dispatch(resetState());
      navigate(DASHBOARD_PATH, { replace: true });
    } catch (e) {
      if (isEdit) failUpdateBudget();
      else failCreateBudget();
    }
  }, [params.id, formData, tab, choosenCampaigns, viewOptions]);

  const getCampaignsProps = useCallback(
    () => ({
      handleConfirm,
    }),
    [handleConfirm],
  );

  return {
    handleClosePopup,
    handleConfirm,
    getCampaignsProps,
  };
}
